import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import { processedCategoryData } from "./Services/reportExpense";

function ReportExpensePerCategory({ expenseData, expenseDataLoading, expenseDataError }) {
  const reportingFinancialExpense = useSelector(
    (state) => state.reportingExpense
  );
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));

  const setReportingCurrency = useSelector((state) => state.reportingExpense.expCurrency);
  var currencies = "USD";
  if (setReportingCurrency && setReportingCurrency.length === 1) {
    currencies = setReportingCurrency[0];
  } else {
    currencies = settings.currency;
  }

  // All hooks must be called before any early returns
  const processedData = useMemo(() => {
    if (!expenseData || expenseData.length === 0) return [];
    return processedCategoryData(
      expenseData,
      reportingFinancialExpense,
      settings,
      currencies,
      forex
    );
  }, [expenseData, reportingFinancialExpense, settings, currencies, forex]);

  const series = processedData?.map((category) => category.amount) || [];
  const labels = processedData?.map((category) => category.name) || [];

  const hasNoData = series.length === 0 || series.every(value => value <= 0);
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    if (hasNoData) {
      // Set a delay of 2 seconds before showing the message
      const timer = setTimeout(() => {
        setShowMessage(true);
      }, 2000);

      // Clear the timer if component unmounts
      return () => clearTimeout(timer);
    } else {
      // Reset message when data is available
      setShowMessage(false);
    }
  }, [hasNoData]);

  // Early returns after all hooks
  if (expenseDataLoading) {
    return (
      <div className="outer-piechart-report">
        <div className="outer-piechart-title outer-piechart-title-desktop">
          Expense per Category
        </div>
        <div className="outer-piechart-section">
          <div className="outer-piechart-title outer-piechart-title-mobile">
            Expense per Category
          </div>
          {/* Donut chart skeleton */}
          <div style={{
            width: '200px',
            height: '235px',
            margin: '0 auto',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Outer donut ring skeleton with colored segments */}
            <div style={{
              width: '193.845px',
              height: '193.845px',
              borderRadius: '50%',
              border: '30px solid',
              borderTopColor: '#8AA9D2',
              borderRightColor: '#234266',
              borderBottomColor: '#6D98CF',
              borderLeftColor: '#4263A5',
              opacity: 0.3,
              animation: 'pulse 1.5s ease-in-out infinite',
              position: 'relative',
              transform: 'rotate(-45deg)',
              boxSizing: 'border-box'
            }}>
              {/* Inner circle (donut hole) */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '133.845px',
                height: '133.845px',
                borderRadius: '50%',
                backgroundColor: '#ffffff',
              }} />
            </div>
          </div>
        </div>
        {/* Legend skeleton */}
        <div className="custom-legend">
          <div className="custom-legend-title">Top 3 Categories</div>
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="custom-legend-item">
              <div className="custom-legend-value">
                <span
                  className="custom-legend-marker"
                  style={{
                    backgroundColor: '#e5e7eb',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${index * 0.1}s`
                  }}
                ></span>
                <div style={{
                  width: '120px',
                  height: '14px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${index * 0.1}s`
                }} />
              </div>
              <div className="custom-llegend-value">
                <div style={{
                  width: '60px',
                  height: '14px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${index * 0.1}s`
                }} />
              </div>
            </div>
          ))}
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  if (expenseDataError) return <div>Error loading data.</div>;

  // Colors array for all clients
  const allColors = [
    "#8AA9D2",
    "#234266",
    "#6D98CF",
    "#4263A5",
    "#6BA9E2",
    "#9ACBFB",
    "#75AECF",
    "#3f4b57",
    "#6A9DCB",
    "#A4C4E6",
    "#9AC9F3",
    "#000",
    "#89A7C4",
    "#8EA9C6",
    "#BBCFE7",
    "#B7D4F0",
    "#A6C0D6",
    "#4B6F98",
    "#A2B5CB",
    "#86A0BC",
  ];

  // Function to get top 3 series and corresponding labels and colors
  const getTop3Series = () => {
    const seriesWithIndices = series.map((value, index) => ({ value, index }));

    seriesWithIndices.sort((a, b) => b.value - a.value);

    const top3Series = seriesWithIndices.slice(0, 3);
    const top3Labels = top3Series.map((item) => labels[item.index]);
    const top3Colors = top3Series.map((item) => allColors[item.index]);

    return { top3Labels, top3Colors };
  };

  const { top3Labels, top3Colors } = getTop3Series();

  const options = {
    chart: {
      width: 193.845,
      height: 193.845,
      type: "donut",
      events: {
        click: (event, chartContext, config) => {
          event.preventDefault(); // Prevent default click behavior
        },
        selection: (event, chartContext, config) => {
          return false; // Disable selection
        },
      },
    },
    plotOptions: {
      pie: {
        startAngle: -45,
        endAngle: 315,
        expandOnClick: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: "gradient",
    },
    legend: {
      show: false,
    },
    labels: labels,
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val) {
          return val + "%";
        },
      },
      custom: function ({ series, seriesIndex, w }) {
        const category = labels[seriesIndex];
        const amount = series[seriesIndex];
        const totalAmount = series.reduce((total, val) => total + val, 0);
        const percentage = ((amount / totalAmount) * 100).toFixed(2);
        return `<div class="apexcharts-tooltip-custom" style="background: #FFFFFF; box-shadow: 0px 1px 8px rgba(110, 110, 110, 0.1); border-radius: 0px;height: 44pxxpx;padding: 5px 10px;">
                  <span class="apexcharts-tooltip-marker"></span>
                  <span class="apexcharts-tooltip-text" style="font-weight: 300; font-size: 11px; line-height: 13.42px; letter-spacing: 0.5px; color: #282828;">${category}</span>
                  <span class="apexcharts-tooltip-value" style="font-weight: 300; font-size: 11px; line-height: 13.42px; letter-spacing: 0.5px; color: #282828;">${new Intl.NumberFormat(
          "en-US",
          { style: "currency", currency: currencies }
        ).format(amount)} - ${percentage}%</span>
                </div>`;
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
    colors: allColors,
    states: {
      active: {
        filter: {
          type: "none", // No visual change for active state
        },
      },
      normal: {
        filter: {
          type: "none", // No visual change for normal state
        },
      },
    },
  };

  // Custom Legend Component
  const CustomLegend = () => {
    return (
      <div className="custom-legend">
        <div className="custom-legend-title">Top 3 Categories</div>
        {top3Labels.map((label, index) => (
          <div key={label} className="custom-legend-item">
            <div className="custom-legend-value">
              <span
                className="custom-legend-marker"
                style={{ backgroundColor: top3Colors[index] }}
              ></span>
              <span className="custom-legend-text">{label}</span>
            </div>
            <div className="custom-llegend-value">
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: currencies,
              }).format(series[labels.indexOf(label)])}
            </div>
          </div>
        ))}
      </div>
    );
  };



  return (
    <div className="outer-piechart-report">
      <div className="outer-piechart-title outer-piechart-title-desktop">
        Expense per Category
      </div>
      <div className="outer-piechart-section">
        <div className="outer-piechart-title outer-piechart-title-mobile">
          Expense per Category
        </div>
        {hasNoData && showMessage ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '235px'
          }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px" }}>
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="10" width="32" height="26" rx="3" stroke="#D0D5DD" strokeWidth="2" />
                <path d="M6 16h32" stroke="#D0D5DD" strokeWidth="2" />
                <path d="M14 7v6M30 7v6" stroke="#D0D5DD" strokeWidth="2" strokeLinecap="round" />
                <path d="M13 24h6M13 29h12" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p style={{ margin: 0, fontSize: "15px", fontWeight: "500", color: "#282828" }}>No data to display</p>
            </div>
          </div>
        ) : (
          <ReactApexChart
            options={options}
            series={series}
            type="donut"
            width={200}
            height={235}
          />
        )}
      </div>
      {!hasNoData && top3Labels.length > 0 && <CustomLegend />}
    </div>
  );
}

export default ReportExpensePerCategory;
